import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Card } from '../Card'
import { InputModal } from '../InputModal'
import { TransactionChecklistForm } from './transaction-checklist-modal-form'
import { kycServices } from '../../../services'
import { RootState } from '../../../store'
import './transactionChecklistModal.scss'
import { EngagementEvents, logCrmEvent } from '../../utils/crmEvents'
import { OpportunityDetails } from '../../types'

interface TransactionChecklistProps {
  active: boolean
  handleOnClose: () => void
  opportunity?: OpportunityDetails
  /** Extra Card classes (e.g. invest-v2 theming) */
  cardClasses?: string[]
  /** Extra class on InputModal root */
  modalContainerClassName?: string
}

const TransactionChecklist = ({
  active,
  handleOnClose,
  opportunity,
  cardClasses = [],
  modalContainerClassName = '',
}: TransactionChecklistProps) => {
  const { authToken, userId } = useSelector((state: RootState) => state.investor)
  const [isChecked, setChecked] = useState<boolean>(false)
  const handleCheckboxClick = async () => {
    const data = await kycServices.acceptTransactionChecklistAgreement(
      { declaration: true },
      {
        Authorization: authToken ? authToken : '',
      },
    )

    if (data && data.data && data.status === 200) {
      setChecked(!isChecked)

      logCrmEvent(
        EngagementEvents.INVESTOR_ACCEPTED_TRANSACTION_CHECKLIST,
        {
          Authorization: authToken ? authToken : '',
        },
        {
          opportunityName: opportunity?.projectName,
          opportunityId: opportunity?.id,
        },
      )

      setTimeout(() => handleOnClose(), 1000)
    }
  }

  useEffect(() => {
    if (opportunity)
      logCrmEvent(
        EngagementEvents.INVESTOR_VIEWED_TRANSACTION_CHECKLIST,
        {
          Authorization: authToken ? authToken : '',
        },
        {
          opportunityName: opportunity?.projectName,
          opportunityId: opportunity?.id,
        },
      )
  }, [opportunity])

  useEffect(() => {
    if (active && !isChecked) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = 'auto'
  }, [active, isChecked])

  useEffect(() => {
    ;(async () => {
      const response = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })
      if (!!response && response.data && response.status === 200) {
        setChecked(response.data.transaction_checklist_agreement)
      }
    })()
  }, [isChecked])

  const handleModalBgClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && isChecked) {
      handleOnClose()
    }
  }

  return active ? (
    <InputModal testId='transaction-checklist-modal' className={modalContainerClassName}>
      <div
        data-testid='transaction-checklist-modal-card-container'
        onClick={handleModalBgClick}
        className='modal-card-container'
      >
        <Card classes={['p-24', 'transaction-checklist-modal', ...cardClasses]}>
          <div
            className='self-declaration-wrapper self-declaration-wrapper-overflow mb-24'
            style={{ maxHeight: 'calc(100% - 110px)' }}
          >
            <div className='self-declaration-wrapper-text'>
              <h1 className='text-align-center mb-8' style={{ lineHeight: '120%' }}>
                Investment Processing Requirements
              </h1>
              <h2 className='transaction-checklist-modal__subtitle text-align-center mb-24'>
                Investment Processing Checklist
              </h2>
              <p>
                Please review the following required steps and terms before transferring funds to
                Credibila.
              </p>

              <h2 className='transaction-checklist-modal__section-title'>Required Steps</h2>
              <ol
                className='transaction-checklist-modal__list'
                data-testid='transaction-checklist-required-steps'
              >
                <li>
                  Verify beneficiary details (name, account number, bank name, etc.) prior to
                  initiating transfer.
                </li>
                <li>Purpose of Transaction must state: “Investment through Credibila Markets”</li>
                <li>
                  Provide proof of payment on the platform, and mark the transaction as completed.
                  This can be either a SWIFT copy, or a transaction reference number if your bank
                  does not issue a SWIFT copy.
                </li>
                <li>
                  The bank account used for the transfer must match the account provided at
                  source-of-funds verification, and must be used for any future withdrawal.
                </li>
              </ol>

              <h2 className='transaction-checklist-modal__section-title'>Terms and Conditions</h2>
              <ol
                className='transaction-checklist-modal__list'
                data-testid='transaction-checklist-terms'
              >
                <li>
                  SWIFT charges are borne by the investor for both deposits and withdrawals. Select
                  the applicable charges option or “sender pays” at the time of deposit. Credibila
                  will deduct these from the withdrawal amount and send the remaining balance to the
                  investor’s account.
                </li>
                <li>
                  Withdrawals can only be processed to the originating account. A change of account
                  requires an account closure letter and proof of the new account in the investor’s
                  name.
                </li>
                <li>
                  In the event of mismatched or closed accounts, a No Objection Certificate (NOC) or
                  account closure letter from the bank must be submitted.
                </li>
                <li>
                  Discrepancies may require submission of a MT199 form from the investor’s bank.
                </li>
                <li>Credibila will never request bank account details or OTPs.</li>
              </ol>
            </div>
          </div>
          <TransactionChecklistForm
            handleCheckboxClick={handleCheckboxClick}
            isChecked={isChecked}
          />
        </Card>
      </div>
    </InputModal>
  ) : null
}
export { TransactionChecklist }
